// Central glossary of plain-language explanations for AquaTrack's technical
// settings and fields. Each entry explains what the parameter *means* — not
// just how to change it. Spread an entry straight into an InfoTip:
//
//     <InfoTip {...TIPS.mcl} />
//
// Definitions are sourced from the Regulatory Rule Library design doc
// (~/aquatrack-rule-library-design.md §5–§12), the federal Safe Drinking
// Water Act (40 CFR 141) and Florida's FAC 62-550, and verified against the
// app's own computed behavior. Keep wording operator-readable: assume the
// reader may not know the acronyms.

import type { InfoTipContent } from "@/components/InfoTip";

export const TIPS = {
  // ── Rule Library: the rule itself ─────────────────────────────────────
  ruleGroup: {
    label: "Contaminant group",
    body: "The regulatory family a contaminant belongs to. Members share monitoring rules and lab methods: Inorganics, Volatile Organics (VOCs), Synthetic Organics (SOCs), Disinfection Byproducts (DBPs), Radiologicals, Microbial, and Secondary (aesthetic) standards.",
  },
  limitType: {
    label: "Limit type",
    body: "What kind of legal limit this is. MCL = Maximum Contaminant Level (enforceable health limit). MRDL = Maximum Residual Disinfectant Level (cap on a disinfectant such as chlorine). Action Level = a concentration that triggers required steps rather than an automatic violation (lead, copper). SMCL = Secondary MCL, a non-enforceable aesthetic guideline (taste, color, odor). Treatment Technique = a required process used when a contaminant can't be measured reliably.",
    cite: "40 CFR 141",
  },
  complianceBasis: {
    label: "Compliance basis",
    body: "How a pass or fail is judged from lab results. Single sample = each result compared directly to the limit. RAA (Running Annual Average) = the average of the most recent four quarters. LRAA (Locational RAA) = a running annual average computed separately at each sampling site (used for disinfection byproducts). % of samples = the share of samples allowed to exceed. Dose = a calculated radiation dose rather than a single concentration.",
  },
  limitValue: {
    label: "Limit value",
    body: "The numeric threshold itself, expressed in the unit at right (e.g. 0.010). Leave blank for limits that are a range or a qualitative requirement — use the 'Limit display' field for those.",
  },
  limitUnit: {
    label: "Unit",
    body: "Unit of the limit. mg/L = milligrams per liter (≈ parts per million); µg/L = micrograms per liter (≈ parts per billion); pCi/L = picocuries per liter (radioactivity); MFL = million asbestos fibers per liter; mrem/yr = annual radiation dose; TON = Threshold Odor Number; plus color units and percent.",
  },
  limitDisplay: {
    label: "Limit display",
    body: "Free-text limit for standards that aren't a single number — a range (pH 6.5–8.5) or a qualitative requirement ('non-corrosive'). Shown to users verbatim and takes the place of the numeric value/unit.",
  },
  detectionLimit: {
    label: "Detection limit (MDL)",
    body: "Method Detection Limit — the lowest concentration the lab method can reliably detect. A result reported below the MDL counts as a non-detect. Used mainly for VOCs and SOCs.",
    cite: "40 CFR 141.24",
  },
  matchDocType: {
    label: "Match doc type",
    body: "Which FDEP filing category proves a test for this contaminant was performed, e.g. 'CHEMICAL AND RADIOLOGICAL RELATED'. The schedule tracker scans documents of this type to auto-detect the last sample date.",
  },
  subjectCodes: {
    label: "Subject codes",
    body: "Whole-word tokens in a filing's subject that mark it as covering this contaminant — e.g. NN (nitrate/nitrite), VOC, SOC, INOR. One filing can carry several codes and satisfy multiple groups at once. Codes match as whole tokens, so 'NN' won't match 'ANNOUNCEMENT'.",
  },
  referenceText: {
    label: "Reference",
    body: "The rule's wording from the source workbook, shown to staff verbatim for context. Not used by any automated check.",
  },
  increasedMonitoring: {
    label: "Increased monitoring / trigger",
    body: "Plain-language note on what tightens monitoring — for example a detection above the RDL bumping a contaminant from annual to quarterly sampling. Reference text only; v1 does not apply it automatically.",
  },
  violationText: {
    label: "Violation determination",
    body: "How a violation is decided for this contaminant, e.g. 'running annual average exceeds the MCL'. Reference text for staff.",
  },
  responseActions: {
    label: "Required response actions",
    body: "What must happen after a failure — confirmation sampling, agency notification, public notice, corrective-action plan. Reference only in v1; verify before relying on it, as the seeded text is not yet fully validated.",
  },
  authorityCite: {
    label: "Authority cite",
    body: "The regulation this rule comes from, e.g. 'FAC 62-550.515' (Florida Administrative Code) or a 40 CFR 141 citation. Recorded for traceability.",
  },
  needsReview: {
    label: "Needs review",
    body: "Flags a value that was seeded or inferred and hasn't been confirmed by a domain expert yet (such as a guessed match code). The list can be filtered to just these.",
  },
  effectiveFrom: {
    label: "Effective from",
    body: "The date this version of the rule takes effect. The engine evaluates each system against whichever version was in force on the date being checked — so a known future change (e.g. the lead Action Level dropping to 0.010 mg/L on the LCRI compliance date, Nov 1, 2027) can be pre-staged and switches on automatically.",
  },
  effectiveTo: {
    label: "Effective to",
    body: "The last date this version applies; set when a newer version supersedes it. Blank means this is the current version.",
  },

  // ── Rule Library: monitoring profiles ─────────────────────────────────
  monitoringProfiles: {
    label: "Monitoring profiles",
    body: "One or more sampling cadences for this contaminant. Each profile is gated by who it applies to (source water, population), so groundwater and surface-water systems can carry different schedules. The engine picks the one matching profile per system.",
  },
  appliesSource: {
    label: "Applies source",
    body: "Limits this cadence to a source-water type. GW = groundwater (wells). SW = surface water. Subpart H = surface water (and GWUDI) subject to the federal filtration/disinfection rules. GWUDI = groundwater under the direct influence of surface water, treated like surface water. Blank = any source.",
  },
  interval: {
    label: "Interval",
    body: "How often a sample is required: annual, quarterly, monthly, daily, every N years, biannual (two 6-month sets), or once per compliance period.",
  },
  alignment: {
    label: "Alignment",
    body: "What calendar the interval is anchored to. Calendar year resets each Jan 1. Compliance period aligns to EPA's Standardized Monitoring Framework — fixed 9-year cycles split into three 3-year periods (the current cycle is 2020–2028). Rolling measures from the last sample date.",
  },
  intervalN: {
    label: "Interval N",
    body: "The N for an 'every N years' interval — e.g. 3 means once every three years.",
  },
  blockYears: {
    label: "Block years",
    body: "Length in years of the compliance-period block this contaminant follows. Most use the 3-year Standardized Monitoring Framework period; radionuclide reduced monitoring runs every 3, 6, or 9 years depending on prior results (40 CFR 141.26).",
  },
  anchorYear: {
    label: "Anchor year",
    body: "The calendar year a compliance-period cycle starts counting from, so 'next due' lands on the correct block boundary (e.g. 2020 for the current framework cycle). Pre-loaded from the framework rather than invented per system.",
  },
  location: {
    label: "Location",
    body: "Where the sample is taken. POE = point of entry (after treatment, entering the distribution system). Distribution = within the pipe network. Taps = consumer taps (lead/copper). Per well / per plant = one sample at each source or plant. Approved-plan sites = the specific points named in a DEP-approved sampling plan.",
  },
  windowMonth: {
    label: "Window month",
    body: "If sampling must fall in a specific month, the month number (1–12). For example nitrate is often sampled in the historically highest-result quarter. Blank = anytime within the period.",
  },
  popRange: {
    label: "Population min / max",
    body: "Restricts this cadence to systems serving a population (people served) in this range. Leave a side blank for open-ended. Larger systems generally sample more frequently.",
  },

  // ── SMTP settings ─────────────────────────────────────────────────────
  smtpHost: {
    label: "Host",
    body: "Address of the outgoing mail server that sends AquaTrack's email (e.g. smtp.gmail.com or your provider's SMTP host).",
  },
  smtpPort: {
    label: "Port",
    body: "Network port the mail server listens on. 587 pairs with STARTTLS, 465 with implicit TLS/SSL, and 25 is the legacy (usually unencrypted) port.",
  },
  smtpSecurity: {
    label: "Security",
    body: "How the connection to the mail server is encrypted. STARTTLS (587) starts plain and upgrades to encrypted — the modern default. Implicit TLS/SSL (465) is encrypted from the first byte. None sends credentials in the clear and should be used only on a trusted internal relay.",
  },
  smtpUsername: {
    label: "Username",
    body: "Login for the mail server, often the full email address. Leave blank only if the server accepts unauthenticated relay (rare).",
  },
  smtpPassword: {
    label: "Password",
    body: "Mail-server password or app-specific password. Stored encrypted; leave blank when editing to keep the existing one.",
  },
  smtpFromEmail: {
    label: "From email",
    body: "The address recipients see in the From line. It must be one the mail server is authorized to send as, or messages may be rejected or marked as spam (SPF/DKIM alignment).",
  },
  smtpFromName: {
    label: "From name",
    body: "Friendly display name shown beside the From address, e.g. 'AquaTrack'. Optional.",
  },

  // ── Per-system attributes (manual flags FDEP doesn't publish) ─────────
  sysAttributes: {
    label: "System attributes",
    body: "Facts about the system that FDEP doesn't publish but that decide which rules apply. Set them so the right monitoring turns on. Leave an answer as 'Unknown' if unsure — the affected rules then show 'needs system info' rather than being guessed as done or overdue.",
  },
  attrChemDisinfectant: {
    label: "Uses chemical disinfectant",
    body: "Whether the system adds a chemical disinfectant such as chlorine or chloramine. Determines whether disinfectant and disinfection-byproduct rules (TTHM, HAA5) apply.",
  },
  attrOzone: {
    label: "Uses ozone",
    body: "Whether the system uses ozone for disinfection or oxidation. Ozone can form bromate, so answering Yes turns on bromate monitoring.",
  },
  attrClo2: {
    label: "Uses chlorine dioxide",
    body: "Whether the system uses chlorine dioxide (ClO₂). It forms chlorite, so Yes turns on chlorite monitoring and the chlorine-dioxide residual rule.",
  },
  attrAsbestos: {
    label: "Asbestos pipe / susceptible",
    body: "Whether the distribution system has asbestos-cement (AC) pipe or is otherwise vulnerable to asbestos — the trigger for asbestos monitoring. FDEP doesn't publish this, so it must be set by hand.",
  },
  attrSource: {
    label: "Water source",
    body: "Confirmed source-water type: ground water (wells) or surface water (Subpart H). FDEP publishes no usable source flag, so until this is confirmed AquaTrack assumes ground water and labels it as assumed. Surface-water systems carry different monitoring rules — confirm this for each system.",
  },
  attrWaivers: {
    label: "Waivers",
    body: "DEP-granted waivers on file that excuse or extend certain monitoring (e.g. a 9-year asbestos waiver, an SOC waiver, a dioxin waiver). List each so the schedule doesn't flag those tests as overdue.",
  },

  // ── Monitoring schedule ───────────────────────────────────────────────
  monSchedule: {
    label: "Monitoring schedule",
    body: "Every drinking-water test required for this system, auto-filled from FDEP filings: what's due, when, where, and whether evidence of a sample was found. This view mirrors the DEP self-tracking form.",
  },
  classification: {
    label: "System classification",
    body: "How FDEP classifies the system, which decides the rules that apply. Class: CWS = Community, NTNCWS = Non-Transient Non-Community, TNCWS = Transient Non-Community. Source: groundwater vs. surface water / Subpart H. Plus the population served.",
  },
  monFrequency: {
    label: "Frequency",
    body: "How often this contaminant must be sampled for this system, taken from the matching monitoring profile.",
  },
  lastSampled: {
    label: "Last sampled",
    body: "The most recent date a matching test filing was found, or a date entered by hand. 'manual' means a person set it — a manual date always overrides the auto-detected one.",
  },
  nextDue: {
    label: "Next due",
    body: "When the next sample is due, computed from the last-sampled date plus the profile's interval and alignment.",
  },
  monStatus: {
    label: "Status",
    body: "Done = a matching filing landed within the current window. Due / Overdue = the window is open or has passed with no matching filing. Needs info = no confident match was found — never treated as compliant and never silently flagged; confirm it or enter a date. (Hover a status to see the reason.)",
  },
  monLimit: {
    label: "Limit",
    body: "The enforceable limit for this contaminant — a value with unit, or a range / qualitative standard.",
  },
  monOverride: {
    label: "Override",
    body: "Manually set or confirm the last-sampled date when the auto-match is wrong or missing. Confirm accepts the detected evidence; setting a date enters one by hand (which wins over auto-detection); Clear reverts to automatic.",
  },

  // ── Wells ─────────────────────────────────────────────────────────────
  aquifer: {
    label: "Aquifer",
    body: "The underground water-bearing formation the well draws from (e.g. the Floridan aquifer). Influences source-water vulnerability and which contaminants are likely.",
  },
  gwudi: {
    label: "Direct influence",
    body: "GWUDI — Groundwater Under the Direct Influence of surface water. A well flagged Yes is regulated like surface water (filtration and disinfection under Subpart H), not as ordinary groundwater.",
  },

  // ── Plants ────────────────────────────────────────────────────────────
  designCapacity: {
    label: "Design capacity",
    body: "The system's permitted design capacity in gallons per day, as published by FDEP. FDEP no longer publishes per-plant capacities.",
  },
  lastInspection: {
    label: "Last inspection",
    body: "Date of the most recent inspection of this plant on record with FDEP.",
  },
  inspection01: {
    label: "01 inspection",
    body: "Date of the system's last FDEP '01' sanitary survey — the periodic comprehensive inspection. Community systems are surveyed every 3 years (non-community every 5); AquaTrack flags a survey older than 5 years.",
  },
  plantActive: {
    label: "Active",
    body: "Whether the plant is currently in service. Inactive plants are on record but not treating water.",
  },

  // ── Storage / tanks ───────────────────────────────────────────────────
  tankType: {
    label: "Tank type",
    body: "Construction or configuration of the tank — e.g. ground storage, elevated, standpipe, or hydropneumatic.",
  },
  tankCapacity: {
    label: "Capacity (gal)",
    body: "Volume this tank holds when full, in gallons.",
  },
  coating: {
    label: "Coating",
    body: "The tank's interior/exterior lining material. Coating type and condition drive recoating and internal-inspection schedules.",
  },
  lastCleaned: {
    label: "Last cleaned",
    body: "When the tank was last cleaned/inspected internally (year-month). More than 5 years ago is flagged — it drives the tank-cleaning alert.",
  },
  totalStorage: {
    label: "Total storage",
    body: "Sum of all finished-water tank capacities for this system, in gallons.",
  },
  hoursMaxOutput: {
    label: "Hours at max design output",
    body: "How many hours total storage would last if drawn down at the system's design rate (total storage × 24 ÷ design gpd). A rough measure of storage resilience / redundancy.",
  },

  // ── Lead Service Line Inventory (LSLI) ────────────────────────────────
  lsli: {
    label: "Lead service line inventory",
    body: "A federally required count of every service line by material, used to find and replace lead lines under the Lead and Copper Rule Revisions.",
    cite: "40 CFR 141.84",
  },
  lslLead: {
    label: "Lead lines",
    body: "Service lines confirmed to be lead — the ones that must be replaced.",
  },
  lslGrr: {
    label: "GRR",
    body: "Galvanized Requiring Replacement — galvanized-iron lines that are, or ever were, downstream of a lead line and so must be replaced as if they were lead.",
  },
  lslUnknown: {
    label: "Unknown lines",
    body: "Service lines whose material hasn't been verified yet. Under the rule these are presumed lead until proven otherwise.",
  },
  lslNonLead: {
    label: "Non-lead lines",
    body: "Service lines confirmed to be a safe material such as copper or plastic.",
  },
  lslSubmitted: {
    label: "Submitted",
    body: "When the inventory was first submitted to FDEP. The initial federal deadline was October 16, 2024.",
  },
  // ── FDEP documents ────────────────────────────────────────────────────
  fdepDocs: {
    label: "FDEP documents",
    body: "Filings on record for this system in FDEP's DepNexus public document portal. The compliance checks read these to detect tests and violations.",
  },
  docType: {
    label: "Document type",
    body: "FDEP's category for the filing, e.g. 'CHEMICAL AND RADIOLOGICAL RELATED', 'NONCOMPLIANCE RELATED', or 'PUBLIC NOTICES'. The schedule tracker keys off this category.",
  },
  docSubject: {
    label: "Subject",
    body: "The filing's subject line. It often contains the contaminant/group codes (NN, VOC, SOC…) used to match the document to a monitoring requirement.",
  },
  docPermit: {
    label: "Permit",
    body: "The associated FDEP permit number for the filing, if any.",
  },
  docFile: {
    label: "File",
    body: "Format and size of the attached document (e.g. PDF and its size).",
  },

  // ── Alerts & badges ───────────────────────────────────────────────────
  severity: {
    label: "Severity",
    body: "How urgent an alert is. HIGH = a likely compliance gap or safety issue needing prompt action; MEDIUM = should be addressed soon; LOW = informational or housekeeping.",
  },
  alertBadges: {
    label: "Alert badges",
    body: "Open alert counts for this system by severity — e.g. '2 H' means two high-severity alerts. 'all clear' means none are open.",
  },
  staleData: {
    label: "Stale",
    body: "The data shown is being served from cache and may be slightly out of date; a fresh copy is fetched in the background.",
  },

  // ── Nitrate / Nitrite compliance ──────────────────────────────────────
  calendarYear: {
    label: "Calendar year",
    body: "The compliance year being evaluated (Jan–Dec). Nitrate/nitrite compliance is tracked per calendar year.",
  },
  nnOutOfCompliance: {
    label: "Out of compliance",
    body: "The calendar year has ended with no nitrate/nitrite test on file — a monitoring violation. Before the year closes a missing test shows as 'Now due' instead; it only becomes out of compliance once the deadline (December 31) has passed.",
  },
  nnNowDue: {
    label: "Now due",
    body: "No nitrate/nitrite test is on file yet for this calendar year, but the year isn't over — the sample is due by December 31. This is a heads-up to get it filed, not yet a violation. If the year ends with no test on file, it becomes 'Out of compliance'.",
  },
  nnTestFailed: {
    label: "Test failed",
    body: "A test was filed this year, but so was a nitrate-related failure document (a noncompliance filing or public notice) — the result or its follow-up may have failed. Open the failure doc to check.",
  },
  nnCompliant: {
    label: "Compliant",
    body: "A nitrate/nitrite test was filed this calendar year and no related failure document was found.",
  },
  nnUnknown: {
    label: "Unknown",
    body: "The system's documents couldn't be evaluated for this year (for example a data-fetch error), so compliance is undetermined — not the same as compliant.",
  },
  nnStates: {
    label: "Compliance states",
    body: "Now due = no test on file yet but the year isn't over (a heads-up, due by Dec 31). Out of compliance = the year closed with no test on file (a monitoring violation). Test failed = a test was filed but so was a related failure document. Compliant = tested this year with no failure document. Unknown = the documents couldn't be evaluated.",
  },
  failureIndicator: {
    label: "Failure indicator",
    body: "A document on file — a noncompliance filing or public notice mentioning nitrate/nitrite — that suggests a failed or contested result. Provided as a lead to investigate, not a definitive violation.",
  },

  // ── System header ─────────────────────────────────────────────────────
  pwsId: {
    label: "PWS ID",
    body: "Public Water System identifier — the unique number FDEP assigns to this system (e.g. 3484132). Used everywhere to look the system up.",
  },
  operator: {
    label: "Operator",
    body: "The party responsible for running the system (the operator of record).",
  },
  systemStatus: {
    label: "Status",
    body: "FDEP's operational status for the system, such as Active or Inactive.",
  },

  // ── List sorting & access ─────────────────────────────────────────────
  sortAlerts: {
    label: "Sort: Alerts",
    body: "Orders systems by alert urgency — most HIGH alerts first, then MEDIUM, then LOW, then by name.",
  },
  tenant: {
    label: "Tenant",
    body: "The utility or organization that owns a water system. Platform staff see every tenant; a tenant user sees only their own.",
  },
  invScope: {
    label: "Scope",
    body: "Whether the invited user is platform staff (can see and manage every tenant) or belongs to a single tenant (sees only that utility's systems).",
  },
  invRole: {
    label: "Role",
    body: "What the user can do. Admin = manage users and settings. Operator = day-to-day data entry and edits. Viewer = read-only access.",
  },
} satisfies Record<string, InfoTipContent>;
